import { useSettings } from "../context/SettingsContext";
import { mediaUrl } from "../lib/api";
import { Gem } from "lucide-react";

const SHAPES = {
  circle: "rounded-full",
  rounded: "rounded-xl",
  square: "rounded-none",
};

export const BrandLogo = ({ size, className = "", logoObject, fallbackSize = 32 }) => {
  const { settings } = useSettings();
  const mainLogo = settings?.logo || {};
  const isExplicitCustom = Boolean(logoObject?.custom && logoObject?.url);
  const activeLogo = isExplicitCustom ? logoObject : mainLogo;
  const theme = settings?.festival_theme || "none";
  const src = (isExplicitCustom ? logoObject?.url : null) || mainLogo?.url || logoObject?.url || settings?.favicon_url || "";
  const shape = logoObject?.shape || activeLogo?.shape || mainLogo?.shape || "circle";
  const zoom = logoObject?.zoom ?? (activeLogo?.zoom ?? (mainLogo?.zoom ?? 1));
  const x = logoObject?.x ?? (activeLogo?.x ?? (mainLogo?.x ?? 0));
  const y = logoObject?.y ?? (activeLogo?.y ?? (mainLogo?.y ?? 0));
  const effectiveSize = size || logoObject?.size || activeLogo?.size || mainLogo?.size || fallbackSize;
  const bgType = logoObject?.bg_type || activeLogo?.bg_type || mainLogo?.bg_type || "transparent";
  const bgColor = logoObject?.bg_color || activeLogo?.bg_color || mainLogo?.bg_color || "transparent";
  const bgPadding = logoObject?.bg_padding ?? (activeLogo?.bg_padding ?? (mainLogo?.bg_padding ?? 0));

  const hasCustomBg = bgType === "color" && bgColor && bgColor !== "transparent";

  let doodle = null;
  if (theme === "christmas") {
    doodle = (
      <span className="absolute -top-[6px] -left-[6px] text-base z-20 pointer-events-none select-none animate-bounce" style={{ transform: "rotate(-15deg)" }}>
        🧑‍🎄
      </span>
    );
  } else if (theme === "diwali") {
    doodle = (
      <span className="absolute -bottom-[4px] -right-[4px] text-xs z-20 pointer-events-none select-none animate-pulse">
        🪔
      </span>
    );
  } else if (theme === "eid") {
    doodle = (
      <span className="absolute -bottom-[4px] -right-[4px] text-xs z-20 pointer-events-none select-none animate-pulse">
        🌙
      </span>
    );
  } else if (theme === "holi") {
    doodle = (
      <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-pink-400/35 via-yellow-400/25 to-blue-400/35 blur-[2px] pointer-events-none z-0 scale-125" />
    );
  } else if (theme === "republic_day") {
    doodle = (
      <span className="absolute -bottom-[4px] -left-[4px] text-xs z-20 pointer-events-none select-none">
        🇮🇳
      </span>
    );
  }

  const renderLogo = () => {
    if (src) {
      return (
        <div
          className={`overflow-hidden shrink-0 relative z-10 flex items-center justify-center ${SHAPES[shape] || SHAPES.circle} ${
            hasCustomBg ? "shadow-2xs" : ""
          }`}
          style={{
            width: effectiveSize,
            height: effectiveSize,
            backgroundColor: hasCustomBg ? bgColor : "transparent",
            padding: hasCustomBg && bgPadding > 0 ? `${bgPadding}px` : undefined,
          }}
          data-testid="brand-logo"
        >
          <img
            src={mediaUrl(src)}
            alt="logo"
            loading="lazy"
            decoding="async"
            draggable={false}
            className="w-full h-full object-contain pointer-events-none transition-transform duration-200"
            style={{ transform: `translate(${x}%, ${y}%) scale(${zoom})` }}
          />
        </div>
      );
    }
    return (
      <Gem
        style={{ width: effectiveSize * 0.85, height: effectiveSize * 0.85 }}
        className="text-[#D4AF37] relative z-10 shrink-0"
        strokeWidth={1.5}
        data-testid="brand-logo"
      />
    );
  };

  return (
    <div className={`relative inline-flex items-center justify-center shrink-0 ${className}`}>
      {doodle}
      {renderLogo()}
    </div>
  );
};
